/**
 * Making the flow's own chrome survive next to a modal dialog.
 *
 * Restoring `pointer-events` on chrome portaled to `document.body` is only half
 * the job. The other half is that the host's modal now considers every click on
 * that chrome to be a click OUTSIDE itself.
 */

import type React from 'react';

/**
 * Props that stop a modal dialog from dismissing itself when the user clicks our
 * chrome.
 *
 * 🔴 WITHOUT THIS, FIXING THE DEAD BUTTONS CREATES A WORSE BUG. Radix's
 * `DismissableLayer` — under every Dialog in this app — listens for `pointerdown`
 * on the DOCUMENT and treats anything outside its own node as "dismiss me". Our
 * callout and progress bar are portaled to `document.body`, so they are outside
 * it by construction: the moment their buttons became clickable again, pressing
 * Next would fire the dialog's outside-pointerdown handler and CLOSE the template
 * composer the flow had just opened. Half-filled form gone, on the click that was
 * supposed to move the user forward.
 *
 * Stopping propagation on our own root keeps the event from ever reaching the
 * document listener. React 18 attaches its own listeners to a portal's container
 * (here `document.body`), which sits BELOW `document` in the tree, so our handler
 * runs first and `stopPropagation` on the native event is enough.
 *
 * 🔴 POINTER EVENTS ONLY — never `onClick`. The buttons inside need their click to
 * propagate normally to React's own delegation, and `click` is dispatched
 * separately from `pointerdown`, so suppressing the latter leaves activation
 * untouched. `mousedown` is included because Radix listens for `pointerdown` but
 * other dismiss implementations in this codebase use `mousedown`.
 *
 * Scoped to our own subtree, so nothing outside the flow's chrome is affected —
 * this is deliberately NOT the blanket-stopPropagation pattern that once wedged
 * the CRM drawer open.
 */
export const modalSafeChromeProps: Pick<
  React.HTMLAttributes<HTMLElement>,
  'onPointerDown' | 'onMouseDown'
> = {
  onPointerDown: (event) => event.stopPropagation(),
  onMouseDown: (event) => event.stopPropagation(),
};
